import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DropDownRemoteSearch } from "./DropDownRemoteSearch";
import currencies from "../utils/currencies.json";
import { TestIDs } from "../tests/types/testIds";

const getCurrencies = () => {
  return Object.values(currencies).map((currency) => ({
    value: currency.code,
    label: `(${currency.symbolNative}) ${currency.name}`,
  }));
};

type Props = {
  value?: string;
  onChange: ({ value, label }: { value: string; label: string }) => void;
  error?: any;
  info?: string;
  className?: string;
};

export const CurrencyPicker: React.FC<Props> = ({
  value,
  onChange,
  error,
  info,
  className,
}) => {
  const { t } = useTranslation();

  const currencyOptions = useMemo(
    () =>
      getCurrencies().map((x) => ({
        ...x,
        "data-testid":
          TestIDs.general.settings.initialPreferencesModal.currency.option(
            x.value,
          ),
      })),
    [],
  );

  const onSearchCurrencies = useCallback(
    async (keyword: string) => {
      return currencyOptions
        .filter(
          (currency) =>
            currency.label.toLowerCase().includes(keyword.toLowerCase()) ||
            currency.value.toLowerCase().includes(keyword.toLowerCase()),
        )
        .map((currency) => ({
          label: currency.label,
          value: currency.value,
          "data-testid":
            TestIDs.general.settings.initialPreferencesModal.currency.option(
              currency.value,
            ),
        }));
    },

    [currencyOptions],
  );

  return (
    <DropDownRemoteSearch
      title={t("settings.general.currency.title")}
      defaultOptions={currencyOptions}
      fetchOptions={onSearchCurrencies}
      value={currencyOptions.find((option) => option.value === value)}
      // @ts-ignore
      onChange={(option: { value: string; label: string }) => onChange(option)}
      info={info}
      valid={!error}
      showSearch
      errorMessage={error?.message}
      className={className}
    />
  );
};
